// src/modules/app-views/services/pages/Init/modals/DataImportModal/index.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { CameraOutlined, ImportOutlined } from '@ant-design/icons';
import GenericModal, { openModal } from '../../GenericModal';
import WebcamCaptureModal from 'layouts/SharedComponents/WebcamCaptureModal';
import translateX from 'utils/translateX';
import axiosService from 'services/axiosService';
import appService from 'services/appService';
import stepUpload from './StepUpload';
import stepMapping from './StepMapping';
import stepSettings from './StepSettings';
import stepSummary from './StepSummary';
import stepProcessing from './StepProcessing';

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const DataImportModal = ({
  show,
  setShow,
  afterClose: externalAfterClose,
  targetModule = 'services',
  moduleLabel = 'Serviços',
  onImportComplete,
}) => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. HOOKS & REFS
  // ─────────────────────────────────────────────────────────────────────────────
  const eventSourceRef = useRef(null);
  const importIdRef = useRef(null);
  const hasStartedExecutionRef = useRef(false);
  const goNextRef = useRef(null);

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. LOCAL VARIABLES I
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. LOCAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const [webcamOpen, setWebcamOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [importId, setImportId] = useState(null);
  const [fileInfo, setFileInfo] = useState(null);
  const [mappings, setMappings] = useState([]);
  const [targetFields, setTargetFields] = useState([]);
  const [aiLoading, setAiLoading] = useState(false);
  const [conflictStrategy, setConflictStrategy] = useState('ignore');
  const [duplicateKey, setDuplicateKey] = useState('');
  const [progress, setProgress] = useState(null);
  const [importStatus, setImportStatus] = useState(null);

  // ─────────────────────────────────────────────────────────────────────────────
  // 5. EFFECTS
  // ─────────────────────────────────────────────────────────────────────────────
  // Cleanup SSE on unmount
  useEffect(() => {
    return () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
    };
  }, []);

  // ─────────────────────────────────────────────────────────────────────────────
  // 6. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────

  // Step 1: File upload (receives goNext from step content to auto-advance)
  const handleFileUpload = useCallback(async (file, goNext) => {
    setUploading(true);

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('targetModule', targetModule);

      const [uploadResult] = await axiosService.post({
        url: '/v1/data-import/upload',
        data: formData,
        returnAsObject: true,
      });

      if (uploadResult?.importId) {
        setImportId(uploadResult.importId);
        importIdRef.current = uploadResult.importId;
        setFileInfo(uploadResult.file);

        // Fetch available target fields from registry
        const [modulesData] = await axiosService.get({
          url: '/v1/data-import/modules',
        });

        const modulesList = Array.isArray(modulesData) ? modulesData : [];
        const mod = modulesList.find(m => m.name === targetModule);
        if (mod) {
          setDuplicateKey(mod.duplicateKey || '');
          if (mod.importableFields) {
            setTargetFields(mod.importableFields);
          }
        }

        appService.message({ flag: 'success', text: translateX('success') });
        goNext();
      }
    } catch (error) {
      appService.notification({
        flag: 'error',
        title: translateX('error'),
        description: error?.message || translateX('error'),
      });
    } finally {
      setUploading(false);
    }

    // Prevent antd default upload behavior
    return false;
  }, [targetModule]);

  // Camera capture: reuse the upload flow
  const handleCameraCapture = useCallback((file) => {
    setWebcamOpen(false);
    handleFileUpload(file, goNextRef.current);
  }, [handleFileUpload]);

  // Step 2: AI mapping
  const requestAiMapping = useCallback(async () => {
    const id = importIdRef.current || importId;
    if (!id) return;

    setAiLoading(true);

    try {
      const [aiResult] = await axiosService.post({
        url: `/v1/data-import/${id}/ai-mapping`,
        data: {},
        returnAsObject: true,
      });

      if (aiResult?.mappings) {
        setMappings(aiResult.mappings);
      }
    } catch (error) {
      appService.notification({
        flag: 'error',
        title: translateX('error'),
        description: error?.message || 'AI mapping failed',
      });

      // Fallback: create empty mappings from file headers
      if (fileInfo?.headers) {
        setMappings(fileInfo.headers.map(h => ({
          sourceField: h,
          targetField: null,
          confidence: 0,
          isManualOverride: false,
        })));
      }
    } finally {
      setAiLoading(false);
    }
  }, [importId, fileInfo]);

  const handleMappingChange = useCallback((sourceField, targetField) => {
    setMappings(prev => prev.map(m => {
      if (m.sourceField === sourceField) {
        return { ...m, targetField: targetField || null, isManualOverride: true, confidence: targetField ? 1 : 0 };
      }
      return m;
    }));
  }, []);

  // Step 2 → 3: Save mappings (GenericModal handles loading via onNext wrapper)
  const saveMappings = useCallback(async () => {
    if (!importId) return false;

    try {
      await axiosService.put({
        url: `/v1/data-import/${importId}/mappings`,
        data: { mappings },
        returnAsObject: true,
      });
      return true;
    } catch (error) {
      appService.notification({ flag: 'error', title: translateX('error'), description: error?.message });
      return false;
    }
  }, [importId, mappings]);

  // Step 3 → 4: Save settings (GenericModal handles loading via onNext wrapper)
  const saveSettings = useCallback(async () => {
    if (!importId) return false;

    try {
      await axiosService.put({
        url: `/v1/data-import/${importId}/settings`,
        data: { conflictStrategy, duplicateKey },
        returnAsObject: true,
      });
      return true;
    } catch (error) {
      appService.notification({ flag: 'error', title: translateX('error'), description: error?.message });
      return false;
    }
  }, [importId, conflictStrategy, duplicateKey]);

  // Step 5: Execute import with SSE progress
  const startExecution = useCallback(async () => {
    if (!importId) return;

    setImportStatus('processing');
    setProgress({ total: fileInfo?.totalRows || 0, processed: 0, imported: 0, updated: 0, skipped: 0, failed: 0 });

    // Connect to SSE for real-time progress
    const backendUrl = appService.getBackendUrl();
    const token = localStorage.getItem('token');
    const sseUrl = `${backendUrl}/v1/data-import/events/${importId}?token=${token}`;

    try {
      const es = new EventSource(sseUrl);
      eventSourceRef.current = es;

      es.addEventListener('import:progress', (event) => {
        try {
          const parsed = JSON.parse(event.data);
          setProgress(parsed.data);
        } catch {
          // Ignore parse errors
        }
      });

      es.addEventListener('import:completed', (event) => {
        try {
          const parsed = JSON.parse(event.data);
          setProgress(parsed.data);
          setImportStatus('completed');
        } catch {
          setImportStatus('completed');
        }
        es.close();
        eventSourceRef.current = null;
      });

      es.addEventListener('import:failed', (event) => {
        try {
          const parsed = JSON.parse(event.data);
          setProgress(prev => ({ ...prev, ...parsed.data }));
        } catch {
          // Ignore
        }
        setImportStatus('failed');
        es.close();
        eventSourceRef.current = null;
      });

      es.onerror = () => {
        es.close();
        eventSourceRef.current = null;
        pollImportStatus();
      };
    } catch {
      // EventSource not available, use polling fallback
      pollImportStatus();
    }

    // Fire the execution request
    try {
      await axiosService.post({
        url: `/v1/data-import/${importId}/execute`,
        data: {},
        returnAsObject: true,
      });
    } catch (error) {
      setImportStatus('failed');
      appService.notification({ flag: 'error', title: translateX('error'), description: error?.message });
    }
  }, [importId, fileInfo]);

  // Polling fallback if SSE fails
  const pollImportStatus = useCallback(async () => {
    if (!importId) return;

    const interval = setInterval(async () => {
      try {
        const [jobData] = await axiosService.get({
          url: `/v1/data-import/${importId}`,
          returnAsObject: true,
        });

        if (jobData?.progress) {
          setProgress(jobData.progress);
        }

        if (jobData?.status === 'completed' || jobData?.status === 'failed') {
          setImportStatus(jobData.status);
          clearInterval(interval);
        }
      } catch {
        clearInterval(interval);
        setImportStatus('failed');
      }
    }, 2000);

    // Auto-clear after 5 minutes
    setTimeout(() => clearInterval(interval), 300000);
  }, [importId]);

  // Reset execution state (called when user navigates back from processing step)
  const resetExecution = useCallback(() => {
    if (importStatus !== 'completed' && importStatus !== 'failed') return;

    setImportStatus(null);
    setProgress(null);
    hasStartedExecutionRef.current = false;

    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }
  }, [importStatus]);

  // onFinish: called when clicking Confirm on last step (Close button)
  const handleFinish = useCallback(async () => {
    if (importStatus === 'completed' || importStatus === 'failed') {
      return true; // Close modal
    }
    return false; // Keep modal open
  }, [importStatus]);

  // afterClose: reset all state after modal animation completes
  const handleAfterClose = useCallback(() => {
    setWebcamOpen(false);
    setUploading(false);
    setImportId(null);
    importIdRef.current = null;
    setFileInfo(null);
    setMappings([]);
    setTargetFields([]);
    setAiLoading(false);
    setConflictStrategy('ignore');
    setDuplicateKey('');
    setProgress(null);
    hasStartedExecutionRef.current = false;

    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }

    if (importStatus === 'completed') {
      onImportComplete?.();
    }

    setImportStatus(null);
    externalAfterClose?.();
  }, [importStatus, onImportComplete, externalAfterClose]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 7. LOCAL VARIABLES II
  // ─────────────────────────────────────────────────────────────────────────────
  const mappedCount = useMemo(() => mappings.filter(m => m.targetField).length, [mappings]);
  const ignoredCount = useMemo(() => mappings.filter(m => !m.targetField).length, [mappings]);
  const isProcessing = importStatus === 'processing';

  // Camera button: only visible on mobile/tablet (step 1, not uploading)
  const customLeftButtons = useMemo(() => {
    if (importId || uploading || appService.isDesktop()) return [];
    return [{
      tooltip: 'take_photo',
      icon: <CameraOutlined />,
      onClick: () => setWebcamOpen(true),
    }];
  }, [importId, uploading]);

  const steps = useMemo(
    () => [
      stepUpload({ uploading, fileInfo, onUpload: handleFileUpload, importIdRef, onGoNextReady: (fn) => { goNextRef.current = fn; } }),
      stepMapping({
        mappings, targetFields, aiLoading, onMappingChange: handleMappingChange,
        importId, requestAiMapping, saveMappings, mappedCount, ignoredCount,
      }),
      stepSettings({ conflictStrategy, setConflictStrategy, duplicateKey, saveSettings }),
      stepSummary({ fileInfo, mappedCount, ignoredCount, conflictStrategy, duplicateKey, resetExecution }),
      stepProcessing({ progress, importStatus, startExecution, hasStartedExecutionRef }),
    ],
    [
      uploading, fileInfo, handleFileUpload,
      mappings, targetFields, aiLoading, handleMappingChange,
      importId, requestAiMapping, saveMappings, mappedCount, ignoredCount,
      conflictStrategy, duplicateKey, saveSettings,
      resetExecution,
      progress, importStatus, startExecution,
    ]
  );

  // ─────────────────────────────────────────────────────────────────────────────
  // 8. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <>
      <GenericModal
        show={show}
        setShow={setShow}
        icon={<ImportOutlined />}
        title={`${translateX('data_import_title')} - ${moduleLabel}`}
        steps={steps}
        onFinish={handleFinish}
        afterClose={handleAfterClose}
        width={680}
        maskClosable={!importStatus && !uploading}
        closable={!isProcessing}
        disableBack={isProcessing}
        confirmDisabled={isProcessing}
        customLeftButtons={customLeftButtons}
      />

      <WebcamCaptureModal
        open={webcamOpen}
        onCancel={() => setWebcamOpen(false)}
        onCapture={handleCameraCapture}
        cam="fullScreen"
      />
    </>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default DataImportModal;

/**
 * Open the data import modal dynamically (for use from menuButton.js)
 */
export function showDataImportModal({ targetModule, moduleLabel, onSuccess } = {}) {
  const WrappedModal = (props) => (
    <DataImportModal
      {...props}
      targetModule={targetModule || 'services'}
      moduleLabel={moduleLabel || 'Serviços'}
      onImportComplete={onSuccess}
    />
  );
  openModal(WrappedModal);
}
